import React, { useState } from "react";
import { CheckSquare2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TaskList } from "@/components/tasks/TaskList";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import type { Task, TaskRelatedType } from "@/types/task";

interface TasksWidgetProps {
  tasks: Task[];
  relatedType: TaskRelatedType;
  relatedId: string;
  relatedName: string;
  onRefresh: () => void;
  canCreate?: boolean;
}

export function TasksWidget({
  tasks,
  relatedType,
  relatedId,
  relatedName,
  onRefresh,
  canCreate = true,
}: TasksWidgetProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const openTasks = tasks.filter((t) => t.task_status === "OPEN");
  const completedTasks = tasks.filter((t) => t.task_status === "DONE");

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
            <CheckSquare2 className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                Tasks & Reminders ({tasks.length})
              </h3>
              {openTasks.length > 0 && (
                <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/30">
                  {openTasks.length} pending
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {completedTasks.length} completed • Follow-ups, calls, and meetings
            </p>
          </div>
        </div>

        {canCreate && (
          <Button
            type="button"
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            className="gap-1.5 self-start sm:self-center h-8 text-xs font-medium"
          >
            <Plus className="size-3.5" />
            <span>Add Task</span>
          </Button>
        )}
      </div>

      {/* Task List */}
      <div className="p-4">
        <TaskList
          tasks={tasks}
          onRefresh={onRefresh}
          emptyMessage="No tasks scheduled. Create a task or reminder to keep track of follow-ups."
          hideRelated={true}
        />
      </div>

      {/* Create Task Dialog */}
      <TaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultRelated={{
          type: relatedType,
          id: relatedId,
          name: relatedName,
        }}
        onSaved={() => {
          setCreateDialogOpen(false);
          onRefresh();
        }}
      />
    </div>
  );
}
